import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

// `customerId` attributes a failed phone dispatch's exception flag to the
// shipment's customer; the shipment page only shows that customer's flags.
type PhoneDispatchRequest = {
	confirmationNumber: string;
	poNumber: string;
	customerId?: number | null;
};

export const postPhoneDispatch = async (
	confirmationData: PhoneDispatchRequest,
) => {
	const url = apiRoutes.POSTPHONEDISPATCH.replace(
		":poNumber",
		confirmationData.poNumber,
	);
	const resp = await postAndValidate(
		url,
		{
			poNumber: confirmationData.poNumber,
			callDate: new Date().toISOString(),
			confirmationNumber: confirmationData.confirmationNumber,
			customerId: confirmationData.customerId,
		},
		z.unknown(),
	);
	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostPhoneDispatch = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (confirmationData: PhoneDispatchRequest) =>
			postPhoneDispatch(confirmationData),
		// Awaiting the refetch keeps the mutation pending (and the spinner up)
		// until the shipment reflects the new state; toast once it's done.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Shipment dispatched.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to dispatch. Please try again.",
			);
			// A failed dispatch adds an exception flag; refetch so its banner shows
			// without a reload. Not awaited so the modal is usable right away.
			void queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
	});

	return {
		postPhoneDispatch: mutate,
		postPhoneDispatchAsync: mutateAsync,
		isPostingPhoneDispatch: isPending,
		error,
		data,
		resetError: reset,
	};
};
